import { Control, useWatch } from 'react-hook-form';
import { FormField, FormItem, FormControl, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { OptimizedCriteriaSection } from '../OptimizedCriteriaSection';
import { calculateReminderDays, formatReminderDays } from '@/utils/reminderCalculator';

interface Step3CustomizationProps {
   
  control: Control<any>;
   
  aiAnalysisData?: any;
  campaignData?: {
    candidate_cv_url?: string;
    job_description_file_url?: string;
    job_description_text?: string;
  };
  onNext: () => void;
  onPrevious: () => void;
  onCancel: () => void;
  onSaveDraft: () => void;
  onAutoSaveDraft?: () => void; // Nouvelle fonction pour sauvegarde automatique
  isSubmitting: boolean;
}

export const Step3Customization = ({
  control,
  aiAnalysisData,
  campaignData,
  onNext,
  onPrevious,
  onCancel: _onCancel,
  onSaveDraft: _onSaveDraft,
  onAutoSaveDraft,
  isSubmitting,
}: Step3CustomizationProps) => {
  // Surveiller les changements de durée de campagne et d'activation des rappels
  const campaignDuration = useWatch({
    control,
    name: 'campaign_duration'
  });

  const remindersEnabled = useWatch({
    control,
    name: 'reminders_enabled'
  });

  // Calculer les jours de rappels selon la durée sélectionnée
  const reminderSettings = campaignDuration ? calculateReminderDays(String(campaignDuration)) : null;
  const formattedReminders = reminderSettings ? formatReminderDays(reminderSettings) : [];

  // Extraire les critères d'évaluation de manière sûre
  const evaluationCriteria = aiAnalysisData && typeof aiAnalysisData === 'object' && 'evaluationCriteria' in aiAnalysisData
     
    ? (aiAnalysisData as Record<string, any>).evaluationCriteria
    : [];

  // Surveiller les critères sélectionnés pour la validation
  const selectedCriteria = useWatch({
    control,
    name: 'selected_evaluation_criteria'
  });

  // Vérifier si tous les champs obligatoires sont remplis
  const isFormValid = campaignDuration && selectedCriteria && selectedCriteria.length >= 5;

  return (
    <div className="flex justify-center">
      <div className="w-full max-w-2xl space-y-8">
        {/* Section Header */}
        <div className="space-y-2">
          <h1 className="text-2xl font-medium font-bricolage text-gray-800">Personnalisation de la campagne</h1>
          <p className="text-sm font-normal font-bricolage text-neutral-400">
            Configurer les critères d'évaluation et les paramètres de campagne
          </p>
        </div>

        {/* Content Section */}
        <div className="space-y-6">
          {/* Critères d'évaluation optimisés */}
          <OptimizedCriteriaSection
            control={control}
            aiGeneratedCriteria={evaluationCriteria}
            campaignData={campaignData}
            onSaveDraft={onAutoSaveDraft || _onSaveDraft}
          />

          {/* Séparateur */}
          <div className="w-full h-0 border-t border-neutral-400/20"></div>

          {/* Section Durée de la campagne */}
          <div className="space-y-4">
            <div className="text-gray-800 text-lg font-medium font-bricolage leading-tight">
              Durée de la campagne
            </div>
            <div className="space-y-2">
              <div className="text-gray-800 text-sm font-medium font-bricolage">
                Durée totale
              </div>
              <FormField
                control={control}
                name="campaign_duration"
                render={({ field }) => (
                  <FormItem>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-11 px-3 py-3.5 bg-white rounded-[10px] border border-neutral-400/20 text-sm font-bricolage text-neutral-400 focus:border-teal-400">
                          <SelectValue placeholder="Choisir la durée" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="3_days">3 jours</SelectItem>
                        <SelectItem value="5_days">5 jours</SelectItem>
                        <SelectItem value="1_week">1 semaine</SelectItem>
                        <SelectItem value="2_weeks">2 semaines</SelectItem>
                        <SelectItem value="1_month">1 mois</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Séparateur */}
          <div className="w-full h-0 border-t border-neutral-400/20"></div>

          {/* Section Rappels automatiques */}
          <div className="space-y-4">
            <div className="text-gray-800 text-lg font-medium font-bricolage leading-tight">
              Rappels automatiques
            </div>

            {/* Toggle des rappels automatiques */}
            <FormField
              control={control}
              name="reminders_enabled"
              render={({ field }) => (
                <FormItem>
                  <div className="h-12 px-3 py-3.5 bg-gray-50 rounded-[10px] border border-neutral-400/20 flex justify-between items-center">
                    <div className="text-gray-800 text-base font-medium font-bricolage">
                      Activer les rappels automatiques
                    </div>
                    <FormControl>
                      <button
                        type="button"
                        onClick={() => field.onChange(!field.value)}
                        className={`relative w-11 h-6 rounded-full transition-colors ${field.value ? 'bg-teal-400' : 'bg-gray-300'
                          }`}
                      >
                        <div
                          className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${field.value ? 'translate-x-5' : 'translate-x-0.5'
                            }`}
                        />
                      </button>
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Détails du calendrier de rappel */}
            {remindersEnabled && (
              <div className="p-3.5 rounded-[10px] border border-neutral-400/20 space-y-3">
                <div className="text-neutral-400 text-xs font-normal font-bricolage">
                  Maximum 1 rappel par jour pour ceux qui n'ont pas encore répondu
                </div>
                <div className="space-y-2.5">
                  <div className="text-gray-800 text-sm font-medium font-bricolage leading-tight">
                    Calendrier de rappel :
                  </div>
                  {campaignDuration ? (
                    <div className="space-y-2.5">
                      {formattedReminders.map((reminder, index) => (
                        <div key={index} className="flex justify-between items-center">
                          <div className="text-neutral-400 text-sm font-medium font-bricolage leading-tight">
                            {reminder.label}
                          </div>
                          <div className="text-gray-800 text-sm font-medium font-bricolage leading-tight">
                            Après {reminder.days} jour{reminder.days > 1 ? 's' : ''} sans réponse
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-neutral-400 text-sm font-normal font-bricolage">
                      Sélectionnez d'abord une durée de campagne
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Message d'aide si le formulaire n'est pas valide */}
        {!isFormValid && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="text-amber-800 text-sm font-medium font-bricolage">
              Pour continuer, veuillez :
            </div>
            <ul className="mt-1 text-amber-700 text-sm font-bricolage list-disc list-inside">
              {!campaignDuration && <li>Sélectionner une durée de campagne</li>}
              {(!selectedCriteria || selectedCriteria.length < 5) && (
                <li>Choisir au moins 5 critères d'évaluation</li>
              )}
            </ul>
          </div>
        )}

        {/* Footer with buttons */}
        <div className="flex justify-between items-center">
          <Button
            type="button"
            variant="outline"
            onClick={onPrevious}
            disabled={isSubmitting}
            className="h-10 px-5 py-3 rounded-[10px] font-bricolage"
          >
            <div className="text-sm font-semibold font-bricolage">
              Précédent
            </div>
          </Button>
          <Button
            type="button"
            onClick={onNext}
            disabled={isSubmitting || !isFormValid}
            className="h-10 px-5 py-3 bg-teal-400 hover:bg-teal-500 rounded-[10px] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="text-white text-sm font-semibold font-bricolage">
              Suivant
            </div>
          </Button>
        </div>
      </div>
    </div>
  );
};